import { useQuery } from "@tanstack/react-query";

import { API_ROUTES } from "../api/ApiRoutes";
import createPrivateAdminPanelInstance from "../api/PrivateAdminPanelInstance";
import type {
  GetStaffParams,
  StaffApiData,
  StaffApiFilters,
  StaffApiRecord,
  StaffApiResponse,
  StaffFilters,
  StaffListResponse,
  StaffOption,
  StaffPagination,
  StaffRecord,
  StaffRole,
  StaffStatus,
} from "../types/staff";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;

/** Map backend status to pill color (backend does not return status_color). */
const STATUS_COLORS: Record<string, string> = {
  active: "#22c55e",
  disabled: "#6b7280",
  temporary: "#f97316",
};
const defaultStatusColor = "#6b7280";

const getUniqueOptions = <TValue extends string>(
  values: TValue[],
): StaffOption<TValue>[] =>
  Array.from(new Set(values)).map((value) => ({
    value,
    label: formatLabel(value),
  }));

function formatLabel(s: string): string {
  if (!s) return s;
  return s
    .split(/[-_\s]+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
}

const fallbackPagination = (
  items: StaffRecord[],
  page: number,
  pageSize: number,
  hasNext: boolean,
): StaffPagination => ({
  current_page: page,
  page_size: pageSize,
  total_items: (page - 1) * pageSize + items.length + (hasNext ? pageSize : 0),
  total_pages: page + (hasNext ? 1 : 0),
  has_next: hasNext,
  has_prev: page > 1,
});

const normaliseStaffRecord = (
  record: StaffApiRecord,
  index: number,
): StaffRecord => {
  const id = record.admin_id ?? `staff-${index}`;
  const statusLower = (record.status ?? "").toLowerCase();
  const statusColor =
    record.status_color ?? STATUS_COLORS[statusLower] ?? defaultStatusColor;
  
  // Support both new format (role_id) and legacy format (role as name)
  const roleId = record.role_id ?? record.role ?? "";
  // Legacy: if role_id doesn't exist, role contains the name; otherwise lookup in component
  const role = record.role_id ? "" : (record.role ?? "");
  const roleColor = record.role_id ? "" : (record.role_color ?? "");
  
  return {
    key: index + 1,
    id,
    ...(record.admin_id != null ? { admin_id: record.admin_id } : {}),
    firstName: record.first_name ?? "",
    lastName: record.last_name ?? "",
    email: record.email ?? "",
    timezone: record.timezone ?? "",
    roleId,
    role,
    roleColor,
    status: record.status ?? "",
    statusColor,
    ...(typeof record.mfa_enrolled === "boolean"
      ? { mfaEnrolled: record.mfa_enrolled }
      : {}),
  };
};

const fetchStaff = async ({
  last_evaluated_key,
  page_size = DEFAULT_PAGE_SIZE,
  search,
  roles,
  statuses,
  page = DEFAULT_PAGE,
}: GetStaffParams): Promise<StaffListResponse> => {
  const token = JSON.parse(localStorage.getItem("token") || "null");
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.AccessToken,
  );

  const response = await PrivateAdminPanelInstance.get(API_ROUTES.GET_STAFF, {
    params: {
      ...(last_evaluated_key != null && last_evaluated_key !== ""
        ? { last_evaluated_key }
        : {}),
      page_size,
      ...(search?.trim() ? { search_str: search.trim() } : {}),
      ...(roles?.length ? { roles: roles.join(",") } : {}),
      ...(statuses?.length ? { statuses: statuses.join(",") } : {}),
    },
  });

  const responsePayload = (response?.data ?? {}) as StaffApiResponse;
  const dataRoot = (responsePayload?.data ?? responsePayload) as StaffApiData;

  const apiItems = (dataRoot?.items ?? []) as StaffApiRecord[];
  const items = apiItems.map(normaliseStaffRecord);
  const nextKey =
    dataRoot?.last_evaluated_key !== undefined
      ? dataRoot.last_evaluated_key
      : null;
  const hasNext = nextKey != null && nextKey !== "";

  const pagination: StaffPagination = fallbackPagination(
    items,
    page,
    page_size,
    hasNext,
  );

  const apiFilters = (dataRoot?.filters ?? {}) as StaffApiFilters;
  const fallbackFilters: StaffFilters = {
    roles: getUniqueOptions(items.map((item) => item.role as StaffRole)),
    status: getUniqueOptions(items.map((item) => item.status as StaffStatus)),
  };

  const filters: StaffFilters = {
    roles: apiFilters.roles ?? fallbackFilters.roles,
    status: apiFilters.status ?? apiFilters.statuses ?? fallbackFilters.status,
  };

  return {
    items,
    pagination,
    filters,
    last_evaluated_key: nextKey,
  };
};

const normaliseArrayForKey = (values?: string[]) =>
  values?.slice().sort().join("|") ?? "";

export const useGetStaff = (params: GetStaffParams = {}) => {
  return useQuery<StaffListResponse, Error>({
    queryKey: [
      "GET_STAFF",
      params.last_evaluated_key ?? "",
      params.page_size ?? DEFAULT_PAGE_SIZE,
      params.search ?? "",
      normaliseArrayForKey(params.roles),
      normaliseArrayForKey(params.statuses),
      params.page ?? DEFAULT_PAGE,
    ],
    queryFn: () => fetchStaff(params),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  });
};
